import React, { useEffect, useRef } from 'react'
import { ReactComponent as Stop } from '../../../assets/svg/Stop.svg'
import { ReactComponent as EditContent } from '../../../assets/svg/EditContent.svg'
import { ReactComponent as DownloadIcon } from '../../../assets/svg/DownloadIcon.svg'
import { ReactComponent as DeleteContentIcon } from '../../../assets/svg/DeleteContentIcon.svg'
import { ReactComponent as NotFoundIcon } from '../../../assets/svg/NotFoundIcon.svg'
import { baseUrl } from '../../../constants/Connections'
import { useTranslation } from 'react-i18next'
import { capitaliseFirstLetter, format_day, translateTargetText } from '../../../utils/util'
import ProgressBar from '../../../components/ProgressBar'
import ContentSurveyQuestion from './ContentSurveyQuestion'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import axios from 'axios'
import { getLiveResults, getSurveyResults, loadYearAnalyticsMetric, stopContent, stopContentSeries } from '../../../server/server'
import ConfirmationModal from '../ConfirmationModal'
import { useUser } from '../../../auth'
import Loading from '../../Loading'
import ContentResultFilter from './ContentResultFilter'
import i18n from '../../../i18n'
import TargetPreviewModal from '../TargetPreviewModal'
import RerunSurveyModal from '../RerunSurveyModal'

function ContentSurveyResults({ content, setContent, setRefresh, deleteContent, hideControlDetails = false, classId = null, analyticsScope = null }) {
    const { user } = useUser()
    const [exporting, setExporting] = useState(false)
    const [results, setResults] = useState([])
    const [survey, setSurvey] = useState(content)
    const [loading, setLoading] = useState(true)
    const [allUsers, setAllUsers] = useState([])
    const [filteredUsers, setFilteredUsers] = useState([])
    const [filteredResults, setFilteredResults] = useState([])
    const [allYears, setAllYears] = useState({})
    const [selectedClass, setSelectedClass] = useState('all')
    const [selectedYear, setSelectedYears] = useState('all')
    const [searchText, setSearchText] = useState('')
    const [resetTrigger, setResetTrigger] = useState(new Date())
    const [initialLoading, setInitialLoading] = useState(true)
    const [targetModalOpen, setTargetModalOpen] = useState(false)
    const [rerunOpen, setRerunOpen] = useState(false)

    const [visibleSections, setVisibleSections] = useState(1)

    const { t, locale } = useTranslation()
    const containerRef = useRef(null)

    useEffect(() => {
        loadSurveyResults()
        loadSurvey()
    }, [])

    useEffect(() => {
        const container = containerRef.current

        const handleScroll = () => {
            const scrollTop = container.scrollTop
            const scrollHeight = container.scrollHeight
            const clientHeight = container.clientHeight

            const scrollBottom = scrollHeight - scrollTop - clientHeight
            const threshold = 100 // Adjust the threshold as needed

            if (scrollBottom < threshold) {
                // The scroll is close to the bottom
                setVisibleSections(visibleSections + 1)
                // Trigger the action you want here, such as fetching more data
            }
        }

        container.addEventListener('scroll', handleScroll)

        return () => {
            container.removeEventListener('scroll', handleScroll)
        }
    }, [visibleSections])

    const doSearch = (text) => {
        if (text === '') {
            setFilteredUsers([...allUsers].map((e) => e.id))
            setFilteredResults(results)
        } else {
            const users = allUsers.filter((e) => `${e.first_name} ${e.last_name}`.toLowerCase().includes(text)).map((e) => e.id)
            // const filteredResults = results[0]?.questions[0]?.answersUsers.filter((e) => users.includes(e.user_id));

            const filteredResults = results.map((section) => ({
                ...section,
                questions: section.questions.map((question) => {
                    const newAnswersUsers = question.answersUsers.filter((e) => users.includes(e.user_id))
                    return {
                        ...question,
                        answersUsers: newAnswersUsers,
                        answers: newAnswersUsers.map((e) => e.answer),
                    }
                }),
            }))
            setFilteredResults(filteredResults)
            setFilteredUsers(users)
        }
        setResetTrigger(new Date())
    }

    useEffect(() => {
        if (!initialLoading) {
            const delayDebounceFn = setTimeout(() => {
                doSearch(searchText.toLowerCase())
            }, 400)
            return () => clearTimeout(delayDebounceFn)
        }
    }, [searchText])

    const loadSurveyResults = async () => {
        const resp = analyticsScope ? await loadYearAnalyticsMetric('survey_results', analyticsScope, { content_id: content.id }) : await getSurveyResults(content.id, classId)
        if (resp.status === 200) {
            const data = resp.data
            console.log(resp.data)
            setResults(data)
            setFilteredResults(data)
            setLoading(false)
            setInitialLoading(false)
        }
    }

    const loadSurvey = async () => {
        const resp = analyticsScope ? await loadYearAnalyticsMetric('content_results', analyticsScope, { content_id: content.id }) : await getLiveResults({ user_id: user.id, content_id: content?.id, refresh_data: false, class_id: classId })
        if (resp.status === 200) {
            const data = resp.data
            const _survey = { ...data.results[0], status: survey.status }
            setSurvey(_survey)
            const users = _survey.users
            let yearsDict = {}

            for (let i = 0; i < users.length; i++) {
                const user = users[i]
                const year = user.year
                const className = user.classesName

                if (!yearsDict[year]) {
                    yearsDict[year] = new Set()
                }
                yearsDict[year].add(className)
            }

            for (const year in yearsDict) {
                yearsDict[year] = Array.from(yearsDict[year])
            }
            setAllUsers(users)
            setFilteredUsers(users.map((e) => e.id))
            setAllYears(yearsDict)
        }
    }

    const submitClassFilter = (filterData) => {
        const className = filterData.class
        const yearName = filterData.year
        let users = []
        if (className === 'all' && yearName === 'all') {
            users = allUsers.map((e) => e.id)
        } else if (yearName !== 'all' && className === 'all') {
            users = allUsers.filter((e) => e.year === yearName).map((e) => e.id)
        } else {
            users = allUsers.filter((e) => e.classesName === className).map((e) => e.id)
        }
        setSearchText('')
        setFilteredUsers(users)
        setSelectedClass(className)
        setSelectedYears(yearName)
        const filteredResults = results.map((section) => ({
            ...section,
            questions: section.questions.map((question) => {
                const newAnswersUsers = question.answersUsers.filter((e) => users.includes(e.user_id))
                return {
                    ...question,
                    answersUsers: newAnswersUsers,
                    answers: newAnswersUsers.map((e) => e.answer),
                }
            }),
        }))
        setFilteredResults(filteredResults)
    }

    const exportData = async () => {
        setExporting(true)
        const token = localStorage.getItem('token')
        const data = content
        var req = {
            method: 'POST',
            url: `${baseUrl}/api/export_dashboard_content`,
            responseType: 'arraybuffer',
            headers: { Authorization: token },
            data: data,
        }
        var reader = new FileReader()

        reader.onload = function (e) {
            window.open(decodeURIComponent(reader.result), '_self', '', false)
        }

        axios(req).then(
            function (response) {
                var contentType = response.headers['content-type']
                var blob = new Blob([response.data], { type: contentType })

                var a = document.createElement('a')
                document.body.appendChild(a)
                a.style = 'display: none'

                var url = window.URL.createObjectURL(blob)
                a.href = url

                a.download = `Content data-${content?.title}.csv`
                a.click()
                setExporting(false)
            },
            function (resp) {}
        )
    }

    const [stopModalOpen, setStopModalOpen] = useState(false)

    // A recurring run asks whether the scheduled runs should go too; a plain
    // run stops straight away as before.
    const stopRun = async (series = false) => {
        const resp = series ? await stopContentSeries({ id: content?.id }) : await stopContent({ id: content?.id })
        setStopModalOpen(false)
        if (resp?.status === 200) {
            setContent({ ...content, status: 'stop' })
            setRefresh(content?.id)
        }
    }
    const stop = () => (content?.repeat_id ? setStopModalOpen(true) : stopRun(false))

    const openTargetModal = () => {
        setTargetModalOpen(true)
    }

    const closeTargetModal = () => {
        setTargetModalOpen(false)
    }

    return (
        <div>
            {!hideControlDetails && (
                <div className="bg-[#edf3ff] w-full p-5 px-6 rounded-xl">
                    <div className="flex justify-between items-center">
                        <h2 className="font-bold text-xl  text-deepPurple text-left capitalize ">{t(content?.type)}</h2>
                        <p className="text-mudPurple text-[16px] capitalize">{t(content?.status)}</p>
                    </div>
                    {content?.status === 'stop' ? (
                        <button
                            className="border pointer fill-mudPurple bg-lightBG hover:fill-white hover:bg-deselectPurple text-mudPurple hover:text-white border-deselectPurple py-3 px-5 rounded-full w-full mt-2 flex justify-center items-center"
                            onClick={exportData}
                            disabled={exporting}
                        >
                            <span className="text-[16px] font-semibold  mr-3">{t('download')}</span> {exporting ? <Loading /> : <DownloadIcon style={{ fill: 'inherit' }} />}
                        </button>
                    ) : (
                        <div className="flex flex-row w-full justify-between items-center mt-4">
                            <div
                                id="stop-survey-button"
                                className="border pointer fill-mudPurple bg-lightBG hover:fill-white hover:bg-deselectPurple text-mudPurple hover:text-white border-deselectPurple py-3 px-5 rounded-full w-52 flex justify-center items-center"
                                onClick={stop}
                            >
                                <span className="text-[16px] font-semibold  mr-3">{t('stop')}</span> <Stop style={{ fill: 'inherit' }} />
                            </div>
                            <Link to={`/content?editId=${content?.id}`} id="edit-survey-button">
                                <div className="border pointer fill-mudPurple bg-lightBG hover:fill-white hover:bg-deselectPurple text-mudPurple hover:text-white border-deselectPurple py-3 px-5 rounded-full w-52 flex justify-center items-center">
                                    <span className="text-[16px] font-semibold  mr-3">{t('edit')}</span> <EditContent style={{ fill: 'inherit' }} />
                                </div>
                            </Link>
                            <button
                                className="border pointer fill-mudPurple bg-lightBG hover:fill-white hover:bg-deselectPurple text-mudPurple hover:text-white border-deselectPurple py-3 px-5 rounded-full w-52 flex justify-center items-center"
                                onClick={exportData}
                                disabled={exporting}
                                id="export-survey-button"
                            >
                                <span className="text-[16px] font-semibold  mr-3">{t('download')}</span> {exporting ? <Loading /> : <DownloadIcon style={{ fill: 'inherit' }} />}
                            </button>
                            <div
                                id="delete-survey-button"
                                onClick={() => deleteContent(content)}
                                className="border pointer fill-deleteRed bg-lightBG hover:fill-white hover:bg-deleteRed text-deleteRed hover:text-white border-deleteRed py-3 px-5 rounded-full w-52 flex justify-center items-center"
                            >
                                <span className="text-[16px] font-semibold  mr-3">{t('delete')}</span> <DeleteContentIcon style={{ fill: 'inherit' }} />
                            </div>
                        </div>
                    )}
                    {content?.type === 'survey' && !!content?.surveyAnalysis && (
                        <div className="flex flex-row w-full gap-3 mt-3">
                            <Link to={`/analytics/pulse/${content?.survey_id || content?.id}`} id="open-analytics-button" className="flex-1">
                                <div className="border pointer bg-deepPurple hover:bg-highlightColor text-highlightColor hover:text-deepPurple border-deepPurple py-3 px-5 rounded-full w-full flex justify-center items-center">
                                    <span className="text-[16px] font-semibold">{t('Open Analytics')}</span>
                                </div>
                            </Link>
                            <button
                                id="rerun-survey-button"
                                className="border pointer bg-deepPurple hover:bg-highlightColor text-highlightColor hover:text-deepPurple border-deepPurple py-3 px-5 rounded-full flex-1 flex justify-center items-center"
                                onClick={() => setRerunOpen(true)}
                            >
                                <span className="text-[16px] font-semibold">{t('Rerun Survey')}</span>
                            </button>
                        </div>
                    )}
                    {!!allUsers.length && content?.target_text !== 'Staff' && (
                        <ContentResultFilter
                            data={allYears}
                            setSearchText={setSearchText}
                            searchText={searchText}
                            submitClassFilter={submitClassFilter}
                            resetTrigger={resetTrigger}
                        />
                    )}
                </div>
            )}

            <div className={`px-2  rounded-xl py-3`}>
                <div
                    className="gray-small-scroll px-5"
                    id="content-survey-results-container"
                    ref={containerRef}
                    style={{ overflowY: 'scroll', minHeight: '50vh', maxHeight: hideControlDetails ? '80vh' : '60vh', paddingTop: hideControlDetails ? '25px' : 0 }}
                >
                    <div className="flex justify-between items-center mb-3">
                        <p className="text-sm text-mudPurple">
                            <span className="text-[#9392ad99]">{t('by')}:</span> {content?.created_by?.first_name} {content?.created_by?.last_name}
                        </p>
                        <button className="text-mudPurple font-medium text-sm" onClick={openTargetModal}>
                            <span className="text-[#9392ad99]">{t('target')}:</span> {!!content?.anonymous ? t('anonymous') : translateTargetText(content?.target_text)}
                        </button>
                        <p>
                            <span className="text-[#9392ad99] text-sm">
                                {t('date')} & {t('time')}:{' '}
                            </span>
                            <span className="text-mudPurple text-sm">{format_day(content?.publicationDate, true, i18n.language)}</span>
                        </p>
                    </div>
                    <div className="single-content-body my-3 flex justify-between items-start">
                        <h2 className="text-deepPurple font-bold text-xl text-left max-w-xl break-words">{content?.title}</h2>
                        <p className="text-right text-sm text-mudPurple font-medium">
                            {content?.answers?.length}/{content?.target_count}
                        </p>
                    </div>
                    <p className="text-sm text-mudPurple text-left mt-3 break-words">{content?.message ?? content?.description}</p>
                    <div className="mt-4">
                        <ProgressBar bgcolor={'#5E639B'} completed={Math.round((content?.answers?.length / content?.target_count ?? 1) * 100)} />
                    </div>

                    {!loading && (
                        <div className="mt-5">
                            {results
                                .filter((i, index) => index < visibleSections)
                                ?.map((section, index) => {
                                    return (
                                        <div className="bg-whiteContrast p-4 rounded-xl my-5" key={index}>
                                            <h3 className="text-left text-mudPurple text-lg font-semibold break-words">{section?.title}</h3>
                                            <p className="text-left text-sm text-mudPurple mt-2 break-words">{section?.description}</p>
                                            <div>
                                                {section?.questions?.map((question, qi) => {
                                                    return (
                                                        <ContentSurveyQuestion
                                                            question={question}
                                                            realAnswers={question.answers ?? []}
                                                            qi={qi}
                                                            index={index}
                                                            survey={survey}
                                                            key={qi}
                                                            filterName={searchText}
                                                            filteredUsers={filteredUsers}
                                                            filteredResults={filteredResults[index]?.questions[qi]}
                                                            classId={classId}
                                                            analyticsScope={analyticsScope}
                                                        />
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    )
                                })}
                        </div>
                    )}
                    {loading && (
                        <div className=" w-full  flex items-center justify-center -mb-[20px]" style={{ height: 'calc(100% - 120px)' }}>
                            <Loading size={32} />
                        </div>
                    )}
                </div>
            </div>
            <TargetPreviewModal modalIsOpen={targetModalOpen} closeModal={closeTargetModal} content={content} />
            <RerunSurveyModal modalIsOpen={rerunOpen} closeModal={() => setRerunOpen(false)} content={content} onDone={() => setRefresh(content?.id)} />
            <ConfirmationModal
                modalIsOpen={stopModalOpen}
                closeModal={() => setStopModalOpen(false)}
                confirmation={() => stopRun(false)}
                confirmButtonText="stopThisRun"
                secondaryConfirmation={() => stopRun(true)}
                secondaryButtonText="stopAndRemoveScheduled"
                message={t('doYouWantToStopThis')}
                subMessage={content?.repeatPeriodText || t('recurringContent')}
            />
        </div>
    )
}

export default ContentSurveyResults
